import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { flushEvents } from '../../lib/events.ts'
import { withArrival } from '../../lib/firstTouch.ts'
import { forgetTeacher, moveTeacherData } from '../../lib/teacherStorage.ts'
import { visitId } from '../../lib/visitId.ts'
import { api, json } from '../client.ts'
import { qk } from '../keys.ts'
import type { Teacher } from '../types.ts'
import { meQuery } from './me.ts'

/**
 * The session lives in an httpOnly cookie, so this query -- not any client-side flag -- is the
 * single source of truth for who she is. With no session she becomes a guest (see `loadMe`),
 * except where `guest: false`: the sign-in and register pages.
 */
export const useMe = ({ guest = true }: { guest?: boolean } = {}) => useQuery(meQuery(guest))

export const useLogin = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async (body: { email: string; password: string }) => {
			const res = await api.auth.login.$post({ json: body })
			return (await json<{ teacher: Teacher }>(res)).teacher
		},
		// Nothing of the guest's moves here: the api asks, once she is in, whether it is hers
		// (`usePendingGuest`). Everything cached was the guest's view, so it is all stale.
		onSuccess: async (teacher) => {
			await qc.invalidateQueries()
			qc.setQueryData(qk.me, teacher)
		},
	})
}

export const useRegister = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async (body: {
			email: string
			password: string
			name: string
			/** Her answer to bringing the guest's work; false makes a new account and leaves the guest alone. */
			bringGuestWork?: boolean
		}) => {
			// Where the visit began, for a teacher who has no guest before her (see withArrival). The visit
			// exists before this request, so the `guest_started` the api writes for her is in it.
			visitId()
			const res = await withArrival((arrival) =>
				api.auth.register.$post({ json: { ...body, arrival } }),
			)
			return (await json<{ teacher: Teacher }>(res)).teacher
		},
		// Without the guest's work this is another teacher: nothing cached may carry over from the guest.
		onSuccess: async (teacher, body) => {
			if (body.bringGuestWork === false) await qc.invalidateQueries()
			qc.setQueryData(qk.me, teacher)
		},
	})
}

export const useLogout = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async () => {
			// Her last few seconds of events still belong to her session; once it ends they would be
			// refused. Never more than a moment: signing out must not hang on a slow events request.
			await Promise.race([flushEvents(), new Promise((done) => setTimeout(done, 1500))])
			await json<{ ok: true }>(await api.auth.logout.$post())
		},
		// Everything cached, and everything kept in this browser, belongs to the teacher who just left.
		onSuccess: () => {
			const me = qc.getQueryData<Teacher | null>(qk.me)
			if (me) forgetTeacher(me.id)
			qc.clear()
		},
	})
}

/** What the browser's guest has, shown to the teacher who just signed in so she can say whether it is hers. */
export type PendingGuest = {
	guestId: string
	items: { kind: 'activity' | 'wordList' | 'plan' | 'source' | 'folder'; title: string }[]
	total: number
	activities: number
	handedIn: number
}

/**
 * Work left in this browser by a guest, offered after a sign-in to an account that exists. The api
 * keeps the guest in a cookie the page cannot read, so this asks it: null when there is nothing to
 * ask about. Asked once per page load; the two answers below settle it.
 */
export const usePendingGuest = (enabled: boolean) =>
	useQuery({
		queryKey: qk.pendingGuest,
		enabled,
		retry: false,
		staleTime: Number.POSITIVE_INFINITY,
		queryFn: async () =>
			(await json<{ pending: PendingGuest | null }>(await api.auth.pending.$get())).pending,
	})

/** "Yes, these are mine": the api moves the guest's work into this account. */
export const useBringPending = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async (guestId: string) => {
			const res = await api.auth.pending.adopt.$post()
			const { brought } = await json<{ brought: { activities: number; total: number } | null }>(res)
			return { guestId, brought }
		},
		// Her work and her browser's class lists, kept under the guest's id, are the account's now;
		// every list is stale. A refusal (the offer ran out) also settles it: there is nothing to ask.
		onSuccess: async ({ guestId }) => {
			const me = qc.getQueryData<Teacher | null>(qk.me)
			if (me) moveTeacherData(guestId, me.id)
			qc.setQueryData(qk.pendingGuest, null)
			await qc.invalidateQueries()
		},
		onError: () => qc.setQueryData(qk.pendingGuest, null),
	})
}

/** "No, leave them": the guest stays as she was, for whoever made her. */
export const useLeavePending = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async () => json<{ ok: true }>(await api.auth.pending.leave.$post()),
		onSuccess: () => qc.setQueryData(qk.pendingGuest, null),
	})
}

/** Which sign-in methods this deployment offers. Changes only with a deploy. */
export const useProviders = () =>
	useQuery({
		queryKey: qk.providers,
		staleTime: Number.POSITIVE_INFINITY,
		queryFn: async () => json<{ google: boolean }>(await api.auth.providers.$get()),
	})
